// Fixtures + mutable in-memory state for the reconciliation (bank-statement
// matching) and processed-files demo screens. Client: client-romil / "Ромил ООД".
// All data is self-contained — do not import from invoices.ts/erp.ts.

import type { JournalLine, MatchedPairPreview, ProcessedFileData } from '../../lib/api';

export const CLIENT_ID = 'client-romil';
export const CLIENT_NAME = 'Ромил ООД';
export const CURRENCY = 'EUR';
export const BANK_NAME = 'УниКредит Булбанк';
export const BANK_IBAN = 'BG80 UNCR 7000 1500 1234 56';

let idCounter = 1;
export const nextId = (prefix: string): string => `${prefix}-${(idCounter++).toString().padStart(4, '0')}`;

export const nowISO = (): string => new Date().toISOString();

const toDMY = (iso: string): string => {
  const [y, m, d] = iso.split('-');
  return `${d}.${m}.${y}`;
};

const splitVat = (amount: number): { net: string; vat: string } => {
  const vat = Math.round((amount - amount / 1.2) * 100) / 100;
  const net = Math.round((amount - vat) * 100) / 100;
  return { net: net.toFixed(2), vat: vat.toFixed(2) };
};

const pickDebitAccount = (vendorName: string): { code: string; name: string } => {
  if (/ОМВ|Лукойл/i.test(vendorName)) return { code: '603', name: 'Разходи за горива' };
  if (/Технополис|Office\s?1/i.test(vendorName)) return { code: '601', name: 'Разходи за материали' };
  if (/Интерлийз/i.test(vendorName)) return { code: '621', name: 'Разходи за наеми' };
  return { code: '602', name: 'Разходи за външни услуги' };
};

export const DEBIT_ACCOUNTS = [
  { code: '601', name: 'Разходи за материали', type: 'expense' },
  { code: '602', name: 'Разходи за външни услуги', type: 'expense' },
  { code: '603', name: 'Разходи за горива', type: 'expense' },
  { code: '621', name: 'Разходи за наеми', type: 'expense' },
  { code: '4531', name: 'ДДС на покупките', type: 'vat' },
];

export const BANK_ACCOUNTS = [
  { code: '503', name: 'Разплащателна сметка в евро - УниКредит Булбанк', type: 'bank' },
  { code: '501', name: 'Каса в лева', type: 'cash' },
];

// ---------------------------------------------------------------------------
// Internal record shapes
// ---------------------------------------------------------------------------

export interface PartyInfoRecord {
  name: string;
  address: string | null;
  tax_id: string | null;
}

export interface TxRecord {
  id: string;
  client_id: string;
  client_name: string;
  file_id: string | null;
  source: string;
  description: string;
  reference: string;
  amount: number; // signed: negative = outgoing, positive = incoming
  booking_ts: string; // ISO date
  counterparty_name: string;
  currency: string;
}

export interface InvRecord {
  id: string;
  client_id: string;
  client_name: string;
  invoice_number: string;
  mirage_invoice_number: string;
  mirage_counterparty_id: number | null;
  filename: string;
  invoice_date: string; // ISO date
  total_amount: number;
  currency: string;
  vendor: PartyInfoRecord;
  customer: PartyInfoRecord;
  type: string;
  status: string;
}

export interface LinkRecord {
  id: string;
  transaction_id: string;
  invoice_id: string;
  match_percentage: number;
  match_success: string;
  status: string;
  is_automatic: boolean;
  linked_at: string;
  match_reason?: string;
  notes?: string;
  is_committed: boolean;
  committed_at: string | null;
  mirage_accrec_id: number | null;
  preview: MatchedPairPreview;
}

// ---------------------------------------------------------------------------
// Chart-of-account preview builder (shared by seed data + auto/manual matches)
// ---------------------------------------------------------------------------

export function buildPreview(opts: {
  vendorName: string;
  invoiceNumber: string;
  invoiceDate: string;
  vendorTaxId?: string | null;
  amount: number;
  period: string;
  documentNumber: string;
  confidence: 'high' | 'medium' | 'low';
  reasoning: string;
}): MatchedPairPreview {
  const { code, name } = pickDebitAccount(opts.vendorName);
  const { net, vat } = splitVat(opts.amount);

  const debit_lines: JournalLine[] = [
    {
      account: code,
      description: name,
      amount: net,
      client_code: opts.vendorTaxId ?? undefined,
      invoice_number: opts.invoiceNumber,
      invoice_date: toDMY(opts.invoiceDate),
      level_acc: 2,
    },
    {
      account: '4531',
      description: 'ДДС на покупките',
      amount: vat,
      level_acc: 1,
    },
  ];

  const credit_lines: JournalLine[] = [
    {
      account: '503',
      description: 'Разплащателна сметка в евро - УниКредит Булбанк',
      amount: opts.amount.toFixed(2),
      level_acc: 1,
    },
  ];

  return {
    debit_lines,
    credit_lines,
    document_number: opts.documentNumber,
    period: opts.period,
    available_debit_accounts: DEBIT_ACCOUNTS,
    available_bank_accounts: BANK_ACCOUNTS,
    suggested_debit_reasoning: opts.reasoning,
    suggested_debit_confidence: opts.confidence,
    suggested_debit_alternatives: opts.confidence !== 'high' ? ['601', '621'] : undefined,
    suggested_bank_reasoning:
      'Транзакцията постъпва от разплащателната сметка на дружеството в УниКредит Булбанк.',
    pending_selection: false,
  };
}

// ---------------------------------------------------------------------------
// June fixtures — one already-processed & fully committed statement
// ---------------------------------------------------------------------------

const juneVendors: { name: string; taxId: string; amount: number; day: string; invoiceNo: string }[] = [
  { name: 'Технополис България ЕАД', taxId: 'BG130568069', amount: 512.4, day: '2026-06-02', invoiceNo: '1000021102' },
  { name: 'Спиди АД', taxId: 'BG121396123', amount: 389.5, day: '2026-06-03', invoiceNo: '2026-0601' },
  { name: 'Интерлийз ЕАД', taxId: 'BG831686320', amount: 691.1, day: '2026-06-05', invoiceNo: '2026-0555' },
  { name: 'ОМВ България ООД', taxId: 'BG130277528', amount: 158.2, day: '2026-06-08', invoiceNo: '3300145' },
  { name: 'Виваком България ЕАД', taxId: 'BG130460283', amount: 98.7, day: '2026-06-10', invoiceNo: '900217744' },
  { name: 'А1 България ЕАД', taxId: 'BG131468980', amount: 55.9, day: '2026-06-12', invoiceNo: '750219981' },
  { name: 'Електрохолд Продажби АД', taxId: 'BG204513179', amount: 201.55, day: '2026-06-15', invoiceNo: '4400987' },
  { name: 'Office 1 Superstore', taxId: 'BG130950616', amount: 74.2, day: '2026-06-20', invoiceNo: '2026-0512' },
  { name: 'Лукойл България ЕООД', taxId: 'BG000175155', amount: 245.0, day: '2026-06-25', invoiceNo: '5501122' },
];

export const files: ProcessedFileData[] = [
  {
    id: 'file-jun-01',
    original_filename: 'unicredit_2026-06.xml',
    mime_type: 'application/xml',
    size_bytes: 18432,
    source: 'unicredit',
    ingested_at: '2026-06-30T09:12:00Z',
    client_id: CLIENT_ID,
    client_name: CLIENT_NAME,
    transaction_count: 9,
    status: 'completed',
    has_committed_links: true,
    committed_link_count: 9,
  },
];

export const transactions: TxRecord[] = [];
export const invoices: InvRecord[] = [];
export const links: LinkRecord[] = [];

let accrecCounter = 88001;
export const nextAccrecId = (): number => accrecCounter++;

juneVendors.forEach((v, idx) => {
  const txId = nextId('txn-jun');
  const invId = nextId('inv-jun');
  const linkId = nextId('link-jun');

  transactions.push({
    id: txId,
    client_id: CLIENT_ID,
    client_name: CLIENT_NAME,
    file_id: 'file-jun-01',
    source: 'unicredit',
    description: `Плащане към ${v.name}`,
    reference: `ПЛАЩАНЕ Ф-РА ${v.invoiceNo}`,
    amount: -v.amount,
    booking_ts: `${v.day}T10:${(15 + idx).toString().padStart(2, '0')}:00Z`,
    counterparty_name: v.name,
    currency: CURRENCY,
  });

  invoices.push({
    id: invId,
    client_id: CLIENT_ID,
    client_name: CLIENT_NAME,
    invoice_number: v.invoiceNo,
    mirage_invoice_number: v.invoiceNo,
    mirage_counterparty_id: 1000 + idx,
    filename: `${v.invoiceNo}.pdf`,
    invoice_date: v.day,
    total_amount: v.amount,
    currency: CURRENCY,
    vendor: { name: v.name, address: null, tax_id: v.taxId },
    customer: { name: CLIENT_NAME, address: null, tax_id: 'BG204558712' },
    type: 'Фактура',
    status: 'confirmed',
  });

  links.push({
    id: linkId,
    transaction_id: txId,
    invoice_id: invId,
    match_percentage: 100,
    match_success: 'auto',
    status: 'COMMITTED',
    is_automatic: true,
    linked_at: `${v.day}T10:30:00Z`,
    is_committed: true,
    committed_at: `${v.day}T11:00:00Z`,
    mirage_accrec_id: nextAccrecId(),
    preview: buildPreview({
      vendorName: v.name,
      invoiceNumber: v.invoiceNo,
      invoiceDate: v.day,
      vendorTaxId: v.taxId,
      amount: v.amount,
      period: '06.2026',
      documentNumber: (idx + 1).toString().padStart(10, '0'),
      confidence: 'high',
      reasoning: `Точно съвпадение по сума и референция с фактура ${v.invoiceNo}.`,
    }),
  });
});

// Мега Строй — deliberately never appears on a bank statement (blocked, per
// the Brain-demo story continuity). Stays unmatched forever in this demo.
invoices.push({
  id: nextId('inv-blocked'),
  client_id: CLIENT_ID,
  client_name: CLIENT_NAME,
  invoice_number: '2026-0777',
  mirage_invoice_number: '2026-0777',
  mirage_counterparty_id: 1099,
  filename: '2026-0777.pdf',
  invoice_date: '2026-07-01',
  total_amount: 8940.0,
  currency: CURRENCY,
  vendor: { name: 'Мега Строй ООД', address: null, tax_id: 'BG115678234' },
  customer: { name: CLIENT_NAME, address: null, tax_id: 'BG204558712' },
  type: 'Фактура',
  status: 'confirmed',
});

// ---------------------------------------------------------------------------
// July scripted import — 12 transactions, seeded but not yet inserted into
// the live arrays until the upload "processing" script completes.
// ---------------------------------------------------------------------------

interface JulySeed {
  vendorName: string;
  vendorTaxId: string;
  amount: number; // signed
  day: string;
  time: string;
  reference: string;
  invoiceNo?: string;
  confidence?: 'high' | 'medium';
  matchPercentage?: number;
  directDebit?: boolean;
  description?: string;
}

const julySeeds: JulySeed[] = [
  {
    vendorName: 'Технополис България ЕАД',
    vendorTaxId: 'BG130568069',
    amount: -3378.0,
    day: '2026-07-01',
    time: '09:14:00',
    reference: 'ПЛАЩАНЕ Ф-РА 1000023841',
    invoiceNo: '1000023841',
    confidence: 'high',
    matchPercentage: 99,
  },
  {
    vendorName: 'Дунав Логистика ЕООД',
    vendorTaxId: 'BG117733221',
    amount: 12500.0,
    day: '2026-07-01',
    time: '14:02:00',
    reference: 'ПРЕВОД ПО ДОГОВОР РОМИЛ',
    description: 'Постъпление от клиент по договор',
  },
  {
    vendorName: 'Спиди АД',
    vendorTaxId: 'BG121396123',
    amount: -4380.0,
    day: '2026-07-02',
    time: '08:40:00',
    reference: 'ПЛАЩАНЕ Ф-РА 2026-0714',
    invoiceNo: '2026-0714',
    confidence: 'high',
    matchPercentage: 97,
  },
  {
    vendorName: 'ОМВ България ООД',
    vendorTaxId: 'BG130277528',
    amount: -142.1,
    day: '2026-07-02',
    time: '17:22:00',
    reference: 'ПЛАЩАНЕ Ф-РА 3300289',
    invoiceNo: '3300289',
    confidence: 'high',
    matchPercentage: 95,
  },
  {
    vendorName: 'Интерлийз ЕАД',
    vendorTaxId: 'BG831686320',
    amount: -691.1,
    day: '2026-07-03',
    time: '06:00:00',
    reference: 'ЗАДЪЛЖЕНИЕ ПО ДОГОВОР ЗА ЛИЗИНГ',
    invoiceNo: '2026-0718',
    confidence: 'high',
    matchPercentage: 96,
    directDebit: true,
  },
  {
    vendorName: 'Виваком България ЕАД',
    vendorTaxId: 'BG130460283',
    amount: -101.42,
    day: '2026-07-03',
    time: '11:05:00',
    reference: 'ПЛАЩАНЕ Ф-РА 900219912',
    invoiceNo: '900219912',
    confidence: 'high',
    matchPercentage: 98,
  },
  {
    vendorName: 'А1 България ЕАД',
    vendorTaxId: 'BG131468980',
    amount: -55.9,
    day: '2026-07-04',
    time: '09:47:00',
    reference: 'ПЛАЩАНЕ Ф-РА 750221430',
    invoiceNo: '750221430',
    confidence: 'high',
    matchPercentage: 99,
  },
  {
    vendorName: 'Електрохолд Продажби АД',
    vendorTaxId: 'BG204513179',
    amount: -214.3,
    day: '2026-07-04',
    time: '12:30:00',
    reference: 'ПЛАЩАНЕ Ф-РА 4401055',
    invoiceNo: '4401055',
    confidence: 'high',
    matchPercentage: 96,
  },
  {
    vendorName: 'УниКредит Булбанк',
    vendorTaxId: '',
    amount: -12.5,
    day: '2026-07-04',
    time: '23:59:00',
    reference: 'ТАКСА ЗА ПОДДРЪЖКА НА СМЕТКА',
    description: 'Месечна такса за поддръжка на разплащателна сметка',
  },
  {
    vendorName: 'Office 1 Superstore',
    vendorTaxId: 'BG130950616',
    amount: -86.75,
    day: '2026-07-05',
    time: '10:10:00',
    reference: 'ПЛАЩАНЕ Ф-РА 2026-0733',
    invoiceNo: '2026-0733',
    confidence: 'high',
    matchPercentage: 97,
  },
  {
    vendorName: 'Лукойл България ЕООД',
    vendorTaxId: 'BG000175155',
    amount: -260.0,
    day: '2026-07-05',
    time: '15:45:00',
    reference: 'ПЛАЩАНЕ Ф-РА 5501340',
    invoiceNo: '5501340',
    confidence: 'high',
    matchPercentage: 95,
  },
  {
    vendorName: 'Строй Инвест ООД',
    vendorTaxId: 'BG124455310',
    amount: -9340.0,
    day: '2026-07-05',
    time: '16:20:00',
    reference: 'СТРОИ ИНВЕСТ ДОГ 3',
    invoiceNo: '2026-0741',
    confidence: 'medium',
    matchPercentage: 78,
  },
];

let julySeeded = false;

/** Inserts the July fixtures into the live arrays under the given file id. */
export function seedJulyImport(fileId: string): void {
  if (julySeeded) return;
  julySeeded = true;

  julySeeds.forEach((seed) => {
    const txId = nextId('txn-jul');

    transactions.push({
      id: txId,
      client_id: CLIENT_ID,
      client_name: CLIENT_NAME,
      file_id: fileId,
      source: 'unicredit',
      description:
        seed.description ?? (seed.directDebit ? `Директен дебит към ${seed.vendorName}` : `Плащане към ${seed.vendorName}`),
      reference: seed.reference,
      amount: seed.amount,
      booking_ts: `${seed.day}T${seed.time}Z`,
      counterparty_name: seed.vendorName,
      currency: CURRENCY,
    });

    if (!seed.invoiceNo) {
      return; // customer receipt / bank fee: no invoice, stays unmatched
    }

    const invId = nextId('inv-jul');
    invoices.push({
      id: invId,
      client_id: CLIENT_ID,
      client_name: CLIENT_NAME,
      invoice_number: seed.invoiceNo,
      mirage_invoice_number: seed.invoiceNo,
      mirage_counterparty_id: null,
      filename: `${seed.invoiceNo}.pdf`,
      invoice_date: seed.day,
      total_amount: Math.abs(seed.amount),
      currency: CURRENCY,
      vendor: { name: seed.vendorName, address: null, tax_id: seed.vendorTaxId || null },
      customer: { name: CLIENT_NAME, address: null, tax_id: 'BG204558712' },
      type: 'Фактура',
      status: 'confirmed',
    });

    const confidence = seed.confidence ?? 'high';
    links.push({
      id: nextId('link-jul'),
      transaction_id: txId,
      invoice_id: invId,
      match_percentage: seed.matchPercentage ?? 100,
      match_success: 'auto',
      status: 'MATCHED',
      is_automatic: true,
      linked_at: `${seed.day}T${seed.time}Z`,
      is_committed: false,
      committed_at: null,
      mirage_accrec_id: null,
      preview: buildPreview({
        vendorName: seed.vendorName,
        invoiceNumber: seed.invoiceNo,
        invoiceDate: seed.day,
        vendorTaxId: seed.vendorTaxId || null,
        amount: Math.abs(seed.amount),
        period: '07.2026',
        documentNumber: (transactions.length + 100).toString().padStart(10, '0'),
        confidence,
        reasoning: seed.directDebit
          ? `Автоматично засечено директно дебитиране по договор за лизинг, съвпада по сума с фактура ${seed.invoiceNo}.`
          : confidence === 'high'
            ? `Точно съвпадение по сума и референция с фактура ${seed.invoiceNo}.`
            : `Референцията в банковото извлечение е изписана неточно ("${seed.reference}"), но сумата съвпада точно с фактура ${seed.invoiceNo}.`,
      }),
    });
  });
}

export const JULY_TRANSACTIONS_COUNT = julySeeds.length;
